import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { PublicLayout } from '../components/PublicLayout';
import { 
  Search, 
  Clock, 
  Calendar, 
  ArrowRight, 
  Tag,
  BookOpen,
  TrendingUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const API_URL = getApiBaseUrl();

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  const currentCategory = searchParams.get('category') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [currentCategory, currentSearch, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories/public`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', '9');
      if (currentCategory) params.append('category', currentCategory);
      if (currentSearch) params.append('search', currentSearch);
      
      const response = await fetch(`${API_URL}/api/blog/posts/public?${params}`);
      const data = await response.json();
      setPosts(data.posts || []);
      setPagination({
        page: data.page,
        pages: data.pages,
        total: data.total
      });
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const search = formData.get('search');
    setSearchParams(prev => {
      if (search) prev.set('search', search);
      else prev.delete('search');
      prev.delete('page');
      return prev;
    });
  };

  const handleCategoryFilter = (slug) => {
    setSearchParams(prev => {
      if (slug === currentCategory) prev.delete('category');
      else prev.set('category', slug);
      prev.delete('page');
      return prev;
    });
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      prev.set('page', newPage);
      return prev;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const featuredPosts = posts.filter(p => p.is_featured).slice(0, 3);
  const regularPosts = posts.filter(p => !featuredPosts.includes(p));

  return (
    <PublicLayout>
      <SEO 
        title="Blog"
        description="Expert insights on property management, landlord guides, tenant resources, and UK rental market trends. Stay informed with LetWise."
        path="/blog"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-primary/20 text-primary border-primary/30 mb-4">
              <BookOpen className="w-3 h-3 mr-1" />
              LetWise Blog
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Property Management Insights
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Expert guides, compliance updates, and market trends for landlords, tenants, and property professionals.
            </p>
            
            {/* Search */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  name="search"
                  defaultValue={currentSearch}
                  placeholder="Search articles..."
                  className="pl-12 pr-4 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/15"
                />
                <Button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide">
            <Button
              variant={!currentCategory ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryFilter('')}
              className="whitespace-nowrap"
            >
              All Posts
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={currentCategory === cat.slug ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryFilter(cat.slug)}
                className="whitespace-nowrap"
                style={currentCategory === cat.slug ? { backgroundColor: cat.color } : {}}
              >
                {cat.name}
                {cat.post_count > 0 && (
                  <span className="ml-1 text-xs opacity-70">({cat.post_count})</span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <CardContent className="p-5">
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-3" />
                    <div className="h-6 bg-slate-200 rounded mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No articles found</h3>
              <p className="text-slate-500 mb-4">Try adjusting your search or filter</p>
              <Button variant="outline" onClick={() => setSearchParams({})}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Featured Posts (only on first page) */}
              {currentPage === 1 && featuredPosts.length > 0 && !currentCategory && !currentSearch && (
                <div className="mb-12">
                  <div className="flex items-center gap-2 mb-6">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold">Featured Articles</h2>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {featuredPosts.map((post, index) => (
                      <Link 
                        key={post.id} 
                        to={`/blog/${post.slug}`}
                        className={index === 0 ? "lg:col-span-2 lg:row-span-2" : ""}
                      >
                        <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow group">
                          <div className={`relative ${index === 0 ? "h-64 lg:h-full" : "h-48"}`}>
                            <img
                              src={post.featured_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                              {post.category && (
                                <Badge 
                                  className="mb-2"
                                  style={{ backgroundColor: post.category.color }}
                                >
                                  {post.category.name}
                                </Badge>
                              )}
                              <h3 className={`font-bold mb-2 ${index === 0 ? "text-2xl" : "text-lg"}`}>
                                {post.title}
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-white/80">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {post.reading_time_minutes} min read
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(post.published_at).toLocaleDateString('en-GB', { 
                                    day: 'numeric', month: 'short', year: 'numeric' 
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Posts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(currentPage === 1 && !currentCategory && !currentSearch ? regularPosts : posts).map(post => (
                  <Link key={post.id} to={`/blog/${post.slug}`}>
                    <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow group">
                      <div className="relative h-48">
                        <img
                          src={post.featured_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {post.category && (
                          <Badge 
                            className="absolute top-3 left-3"
                            style={{ backgroundColor: post.category.color }}
                          >
                            {post.category.name}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-5">
                        <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.reading_time_minutes} min
                          </span>
                          <span className="flex items-center gap-1 text-primary font-medium group-hover:gap-2 transition-all">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm text-slate-600">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
